/*
  eslint-disable
    no-underscore-dangle,
    @typescript-eslint/no-unsafe-call,
    @typescript-eslint/no-unsafe-assignment,
    @typescript-eslint/no-unsafe-member-access
*/

import ProductData from '../../model/product-data';
import ImageData from '../../model/image-data';
import MessageHelper from '../../helpers/message-helper';
import AsyncPrices from '../product/async-prices';

interface AutocompleteItem {
  url?: string;
  price?: string;
  code?: string;
  name?: string | undefined;
  image?: ImageData | undefined;
  isExactMatch?: boolean;
  label?: string;
  noResults?: boolean;
  notClickable?: boolean;
}

interface OptionsData {
  autocompleteUrl: string;
  minCharactersBeforeRequest: number;
  waitTimeBeforeRequest: number;
  displayProductImages: string;
  appendTo: string;
  isCustomerPricingActivated: boolean;
}

export default class Autocomplete {

  private readonly cache = {};
  private readonly options: OptionsData;
  private readonly searchForm: JQuery;
  private readonly searchSuggestionsLoader: JQuery;

  constructor(private search: JQuery) {
    this.options = this.search.data('options');
    this.searchForm = this.search.closest('.js-autocompleteSearchForm');
    this.searchSuggestionsLoader = this.searchForm.find('.js-site-search-suggestions-loader');
    this.bindSearchAutocomplete();
    this.bindDisableSearch();
    this.bindFormPreventSubmit();
  }

  bindSearchAutocomplete(): void {
    this.search.autocomplete({
      minLength: this.options.minCharactersBeforeRequest,
      delay: this.options.waitTimeBeforeRequest,
      appendTo: this.options.appendTo,

      focus() {
        // prevent textfield value replacement on item focus
        return false;
      },
      select(event, ui) {
        if (!ui.item.url) {
          return false;
        }
        // eslint-disable-next-line no-param-reassign
        ui.item.value = ACC.sanitizer.sanitize(ui.item.value, false);
        window.location.href = ui.item.url;
        return false;
      },

      source: (request, response): void => {
        this.searchSuggestionsLoader.show();
        const term: string = request.term.toLowerCase();
        if (term in this.cache) {
          response(this.cache[term]);
          return;
        }

        $.getJSON(this.options.autocompleteUrl, { term: request.term }, (data) => {
          const suggestionItems: AutocompleteItem[] = [];
          if (data.productCodeSearchProducts != null) {
            if (data.products.length > 0 && data.productCodeSearchProducts.length > 0) {
              suggestionItems.push({
                label: `${MessageHelper.getMessage('eparts.search.productcodesearch.message')}`,
                notClickable: true,
              });
            }

            $.each(data.productCodeSearchProducts, (i, obj: ProductData) => {
              suggestionItems.push({
                code: obj.code,
                name: obj.name,
                url: `${window.ACC.config.encodedContextPath}${obj.url as string}`,
                price: obj.priceWithVat?.formattedValue ?? '',
                image: obj.images?.find((i) => i.format === 'thumbnail'),
                isExactMatch: obj.exactMatch,
              });
            });
          }
          if (data.products != null) {
            if (data.products.length > 0 && data.productCodeSearchProducts.length > 0) {
              suggestionItems.push({
                label: `${MessageHelper.getMessage('eparts.search.freetextsearch.message')}`,
                notClickable: true,
              });
            }
            $.each(data.products, (i, obj: ProductData) => {
              suggestionItems.push({
                code: obj.code,
                name: obj.name,
                url: `${window.ACC.config.encodedContextPath}${obj.url as string}`,
                price: obj.priceWithVat?.formattedValue ?? '',
                image: obj.images?.find((i) => i.format === 'thumbnail'),
                isExactMatch: obj.exactMatch,
              });
            });

            if (data.products.length || data.productCodeSearchProducts.length) {
              suggestionItems.push({
                url: `${window.ACC.config.encodedContextPath}/search?text=${request.term as string}`,
                label: `${MessageHelper.getMessage('common.viewAll')} <i class="ml-5 fa fa-chevron-right"></i>`,
              });
            } else {
              suggestionItems.push({
                label: MessageHelper.getMessage('eparts.search.noresults.message'),
                noResults: true,
                notClickable: true,
              });
            }
          }
          this.searchForm.attr('action', `${window.ACC.config.encodedContextPath}/search`);
          const exactMatchProduct = suggestionItems.filter((info) => info.isExactMatch === true);
          if (exactMatchProduct.length !== 0 && exactMatchProduct[0] !== undefined) {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            this.searchForm.attr('action', `${window.ACC.config.encodedContextPath}/p/${exactMatchProduct[0]?.code}`);
          }
          this.cache[term] = suggestionItems;
          response(suggestionItems);
        }).then(() => {
          this.searchSuggestionsLoader.hide();
        }).catch(() => {
          /* Ignore */
        });
      },
    });

    this.search.data('ui-autocomplete')._renderMenu = function _renderMenu(ul, items) {
      $.each(items, (index, item) => {
        this._renderItemData(ul, item);
      });

      AsyncPrices.init(ul);
    };

    const { isCustomerPricingActivated } = this.options;
    this.search.data('ui-autocomplete')._renderItem = function _renderItem(ul: JQuery<HTMLUListElement>, item: AutocompleteItem) {
      ul.addClass('autocomplete-list');

      const listItemWrapper = $('<li>', {
        class: 'autocomplete-item',
      });

      if (item.notClickable) {
        listItemWrapper.addClass('autocomplete-item__text');
      }

      if (item.noResults) {
        const label = $('<span>', {
          class: 'autocomplete-item__link',
        });
        label.text(item.label as string);

        return listItemWrapper
          .append(label)
          .appendTo(ul);
      }

      const link = $('<a>', {
        href: item.url,
        class: 'autocomplete-item__link',
      });

      if (item.label) {
        link.html(item.label);
        link.addClass('-view-all');
      } else if (item.code) {
        listItemWrapper.addClass('js-asyncPrice');
        listItemWrapper[0].dataset.code = item.code;

        const image = $('<img/>', {
          class: 'autocomplete-item__image',
        });
        image.attr('src', item.image ? item.image.url : `${window.ACC.config.commonResourcePath}/images/missing-product.png`);
        link.append(image);

        const details = $('<div>', { class: 'autocomplete-item__details' });
        link.append(details);

        const code = $('<div>', {
          class: 'autocomplete-item__code',
        });
        details.append(code);

        const codeWrapper = $('<span>', {
          class: item.isExactMatch ? 'text-highlighted ws-pre' : 'ws-pre',
          html: item.code,
        });
        code.append(codeWrapper);

        const name = $('<div/>', {
          class: 'autocomplete-item__name',
          html: item.name ?? '',
        });
        details.append(name);

        const price = $('<div>', {
          class: 'autocomplete-item__price js-asyncPriceValue',
          html: isCustomerPricingActivated ? item.price : '',
        });
        if (!isCustomerPricingActivated) {
          price[0].dataset.asyncKey = 'priceExclVat';
          price[0].dataset.currencyCode = 'true';
        }
        details.append(price);
      }

      return listItemWrapper
        .append(link)
        .appendTo(ul);
    };
  }

  bindDisableSearch(): void {
    this.search
      .keyup(() => {
        const rawValue = this.search.val() as string;
        this.search.val(rawValue.replace(/^\s+/gm, ''));
      });
  }

  bindFormPreventSubmit(): void {
    this.searchForm.submit((e) => {
      if (this.searchSuggestionsLoader.is(':visible')) {
        e.preventDefault();
      }
    });
  }
}
